/**
 * Hash-tag parser for negotiation comment visibility shortcuts.
 *
 * Parses comment text starting with #tagname to determine visibility:
 * - #intern → INTERNAL (reserved)
 * - #alle   → ALL (reserved)
 * - #dnb    → SPECIFIC (known party tag)
 * - #ukjent → SPECIFIC + isUnknown (unknown tag)
 * - No #    → null (not a hash-tag comment)
 */

export interface ParsedHashTag {
  /** Lowercase tag name (without #) */
  tag: string
  /** Resolved visibility type */
  visibility: 'INTERNAL' | 'ALL' | 'SPECIFIC'
  /** Comment body after the #tag */
  body: string
  /** True if tag is a reserved word (#intern, #alle) */
  isReserved: boolean
  /** True if tag doesn't match any known party */
  isUnknown: boolean
}

const RESERVED_TAGS: Record<string, 'INTERNAL' | 'ALL'> = {
  intern: 'INTERNAL',
  alle: 'ALL',
}

const TAG_REGEX = /^#([a-zA-Z0-9_]{1,15})\b/

/**
 * Parse a comment text for #-tag visibility shortcut.
 *
 * @param text      Raw comment text (e.g. "#intern Vi kan gå ned til 14 dager")
 * @param knownTags Array of known party tag_names (lowercase)
 * @returns         Parsed result, or null if text doesn't start with #
 */
export function parseHashTag(
  text: string,
  knownTags: string[],
): ParsedHashTag | null {
  const trimmed = text.trim()
  if (!trimmed.startsWith('#')) return null

  const match = trimmed.match(TAG_REGEX)
  if (!match) return null

  const tag = match[1].toLowerCase()
  const body = trimmed.slice(match[0].length).trim()

  // Check reserved tags
  const reservedVisibility = RESERVED_TAGS[tag]
  if (reservedVisibility) {
    return {
      tag,
      visibility: reservedVisibility,
      body,
      isReserved: true,
      isUnknown: false,
    }
  }

  // Check known party tags (case-insensitive)
  const knownLower = knownTags.map((t) => t.toLowerCase())
  const isKnown = knownLower.includes(tag)

  return {
    tag,
    visibility: 'SPECIFIC',
    body,
    isReserved: false,
    isUnknown: !isKnown,
  }
}
